import {
  Button,
  EmptyStates,
  Flex,
  Skeleton,
  Stack,
  Title,
  VisuallyHidden,
} from '@inflearn/ds-react-next';
import { KoOnly } from '@inflearn/i18n-tools';

import { useTranslation } from 'next-i18next';
import type { ReactElement } from 'react';
import { getCdnTranslationProps } from '../../../utils/server/i18n/getCdnTranslationProps';

import { OrderCard } from '../../../components/pages/my/orders/OrderCard';
import OrderDTO from '../../../components/pages/my/orders/OrderDTO';
import { OrderSummaryCard } from '../../../components/pages/my/orders/OrderSummaryCard';
import useClientApiOrderSummariesQuery from '../../../queries/useClientApiOrderSummariesQuery';

import { ChannelTalk } from '../../../components/common/ChannelTalk';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { COURSE_URL } from '../../../constants/urls';

import { CourseRegistrationManagerLayout } from '../../../FSD/shared/ui/layout/CourseRegistrationManagerLayout';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await getCdnTranslationProps(locale, ['common'])),
  },
  revalidate: 60,
});

export default function OrdersPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useClientApiOrderSummariesQuery();
  const orders = data?.items?.map((order) => new OrderDTO(order));

  const render = () => {
    if (isLoading) {
      return (
        <DeferredComponent>
          <Stack gap={20}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} height={113} radius="md" width="100%" />
            ))}
          </Stack>
        </DeferredComponent>
      );
    }

    if (isError) {
      return (
        <Flex h="369px" align="center" justify="center">
          <EmptyStates
            image="ERROR"
            title={t('문제가 발생했어요')}
            body={t('새로고침 후 문제가 지속되면 지원팀에 문의해 주세요.')}
          />
        </Flex>
      );
    }

    if (data?.items?.length === 0) {
      return (
        <Flex h="369px" align="center" justify="center">
          <EmptyStates
            image="NO_DATA"
            title={t('아직 구매 내역이 없어요')}
            body={t('나를 성장시켜줄 새로운 지식을 찾아보세요.')}
            cta={
              <Button
                variant="default"
                size="xs"
                radius="md"
                component="a"
                href={COURSE_URL.COURSES({ types: 'ONLINE' })}
              >
                {t('강의 리스트 보기')}
              </Button>
            }
          />
        </Flex>
      );
    }

    return (
      <Stack gap="lg">
        <OrderSummaryCard
          data={{
            totalCourseCount: data?.statistics?.totalCount ?? 0,
            totalOrderAmount: data?.statistics?.totalOrderAmount ?? 0,
            totalDiscountAmount: data?.statistics?.totalDiscountAmount ?? 0,
            totalPaymentAmount: data?.statistics?.totalPaymentAmount ?? 0,
          }}
        />
        <Flex component="ul" gap="lg" direction="column">
          {orders?.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </Flex>
      </Stack>
    );
  };

  return (
    <main>
      <VisuallyHidden>
        <Title size="h4" order={2} mb={12}>
          {t('구매 내역')}
        </Title>
      </VisuallyHidden>
      {render()}
      <KoOnly>
        <ChannelTalk />
      </KoOnly>
    </main>
  );
}

function OrdersPageLayout({ children }: { children: ReactElement }) {
  const { t } = useTranslation();
  return (
    <CourseRegistrationManagerLayout
      title={t('메타.구매내역.제목')}
      description={t('메타.구매내역.설명')}
    >
      {children}
    </CourseRegistrationManagerLayout>
  );
}

OrdersPage.getLayout = (page: ReactElement) => <OrdersPageLayout>{page}</OrdersPageLayout>;
